import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SummaryCardAction, SummaryCardComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { BreakpointServiceInjector } from '@next/core-lib/responsive';

import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger } from '@angular/material/menu';
import { RecalculateButtonComponent } from '../../../shared/components/recalculate-button/recalculate-button.component';
import { DiseaseHeaderComponent } from '../../../shared/components/widget-components/header.component';
import { DiseaseDashboardWidgetTableComponent } from '../../../shared/components/widget-components/table.component';
import { DateService } from '../../../shared/services/date.service';
import { CropYearService } from '../../../shared/services/crop-year.service';
import { areDateRangesSameDay, getDefaultDiseaseWidgetSelectedDates } from '../../../shared/utils/date-range.utils';
import { VirusMonitorSignalStore } from '../../signal-store/virus-monitor-signal-store';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner.component/loading-spinner.component';
import { EmptyWidgetComponent } from '../../../shared/components/empty-widget.component';
import { VirusMonitorActionMenuComponent } from '../../shared/action-menu.component';
import { CropFieldAdvice } from '@app/shared/components/disease-overview-table.component/models/models';

@Component({
  selector: 'app-virus-monitor-dashboard-widget',
  templateUrl: './virus-monitor-dashboard-widget.component.html',
  styleUrls: ['./virus-monitor-dashboard-widget.component.scss'],
  imports: [
    SummaryCardComponent,
    DiseaseHeaderComponent,
    MatDivider,
    MatButton,
    MatMenuTrigger,
    MatIcon,
    RecalculateButtonComponent,
    VirusMonitorActionMenuComponent,
    DiseaseDashboardWidgetTableComponent,
    LoadingSpinnerComponent,
    EmptyWidgetComponent,
    TranslatePipe,
  ],
})
export class VirusMonitorDashboardWidgetComponent {
  private readonly localeService = inject(LocaleService);
  private readonly breakpointService = inject(BreakpointServiceInjector);
  private readonly dateService = inject(DateService);
  private readonly router = inject(Router);

  private readonly cropYearService = inject(CropYearService);
  protected readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  protected readonly store = inject(VirusMonitorSignalStore);
  protected readonly translationPrefix = 'virusMonitor';

  protected actions = [
    {
      id: 'info',
      icon: 'info_outline',
      callback: () => window.open('https://share.articulate.com/LPS1lvLY6VrN3X7tQ7QNi'),
    },
  ] satisfies SummaryCardAction[];

  protected readonly selectedCropFieldAdvices = signal<CropFieldAdvice[]>([]);
  protected readonly selectedDates = signal<Date[]>(
    getDefaultDiseaseWidgetSelectedDates(this.dateService, this.store.cropYear()),
  );
  protected readonly hasVirusMonitorAdvices = computed(() => this.store.virusMonitorAdvices().length > 0);
  protected readonly isRecalculationDisabled = computed(
    () =>
      this.store.isVirusMonitorCropFieldsSyncing() ||
      !this.hasVirusMonitorAdvices() ||
      !this.store.hasEligibleVirusMonitorCropFields(),
  );

  private readonly ltMd = toSignal(this.breakpointService.ltMd$, { initialValue: false });
  protected readonly showAdviceButton = computed(() => !this.ltMd());

  private readonly amountOfSelectedCropFields = computed(() => this.selectedCropFieldAdvices().length);
  protected readonly isActionDisabled = computed(() => this.amountOfSelectedCropFields() === 0);
  protected readonly actionButtonLabel = computed(() => {
    const count = this.amountOfSelectedCropFields();
    return count > 0
      ? this.localeService.translate('virusMonitor.dashboardWidget.addAction', { count: count.toString() })
      : this.localeService.translate('virusMonitor.dashboardWidget.addActionDisabled');
  });

  protected onSelectedDatesChange(dates: Date[]): void {
    if (dates.length < 2 || areDateRangesSameDay(this.dateService, dates, this.selectedDates())) {
      return;
    }

    this.store.patchSelectedDates(dates);
    this.selectedDates.set(dates);
  }

  protected onCropFieldSelectionChange(advices: CropFieldAdvice[]): void {
    this.selectedCropFieldAdvices.set(advices);
  }

  protected async onViewAdvice(): Promise<void> {
    await this.router.navigate(['/virus-monitor']);
  }

  protected onAction(_buttonClicked: 'top' | 'bottom'): void {
    // Placeholder action by requirement.
  }

  protected onRecalculate(): void {
    this.store.recalculateVirusMonitor(this.store.eligibleVirusMonitorCropFieldIds());
  }
}
